import { useRef, type ReactNode } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { LW_DS_CLASS } from './LwThemeScope';
import { useScrollLock, useWindowEscapeFallback } from './hooks';

export interface LwConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  onConfirm: () => void;
  destructive?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  returnFocusTo?: HTMLElement | null;
}

/**
 * AlertDialog-based confirmation shell for future screen redesign units.
 * Existing dismissible confirmation flows intentionally remain on LwModal
 * until their owning screen adopts this stronger interaction contract.
 */
export function LwConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  destructive = false,
  disabled = false,
  children,
  returnFocusTo
}: LwConfirmDialogProps) {
  useScrollLock(open);
  const contentRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (open && !wasOpenRef.current && typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
    returnFocusRef.current = document.activeElement;
  }
  wasOpenRef.current = open;
  useWindowEscapeFallback(contentRef, () => onOpenChange(false));

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <div className={LW_DS_CLASS}>
          <AlertDialogPrimitive.Overlay className="scrim" />
          <div className="modal">
            <AlertDialogPrimitive.Content
              asChild
              id={undefined}
              {...(!description ? { 'aria-describedby': undefined } : {})}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                const returnTarget = returnFocusTo && document.contains(returnFocusTo)
                  ? returnFocusTo
                  : returnFocusRef.current;
                if (returnTarget && document.contains(returnTarget)) {
                  returnTarget.focus();
                }
              }}
            >
              <div ref={contentRef} className="modal-card lw-confirm-dialog" data-lw-overlay-layer="">
                <div className="modal-head">
                  <AlertDialogPrimitive.Title asChild>
                    <div className="modal-title">{title}</div>
                  </AlertDialogPrimitive.Title>
                  {description && (
                    <AlertDialogPrimitive.Description asChild>
                      <div className="modal-sub">{description}</div>
                    </AlertDialogPrimitive.Description>
                  )}
                </div>
                {children && <div className="modal-body">{children}</div>}
                <div className="modal-foot">
                  <AlertDialogPrimitive.Cancel asChild>
                    <button className="btn-sm" type="button">
                      {cancelLabel}
                    </button>
                  </AlertDialogPrimitive.Cancel>
                  <AlertDialogPrimitive.Action asChild>
                    <button
                      className={`btn-sm${destructive ? ' danger' : ' solid'}`}
                      type="button"
                      onClick={onConfirm}
                      disabled={disabled}
                    >
                      {confirmLabel}
                    </button>
                  </AlertDialogPrimitive.Action>
                </div>
              </div>
            </AlertDialogPrimitive.Content>
          </div>
        </div>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
